import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { periodFromParams } from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/CommandsFilterBar/ViewSelector/periodFromParams'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import type { GroupType, ViewType } from 'modules/shift_management/store/internal/models/View'
import View from 'modules/shift_management/store/internal/models/View'
import {
  DEFAULT_GROUP_TYPE,
  DEFAULT_VIEW_TYPE,
  isValidGroupType,
  isValidViewType,
} from 'modules/shift_management/store/internal/models/View'

const useChangeView = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [, dispatchSchedule] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const [, dispatchPagination] = usePagination()
  const [, dispatchFilters] = useFilters()
  const [, dispatchShiftsRows] = useShiftsRows()
  const [, dispatchUI] = useUI()

  const localStorageKey = LOCAL_STORAGE_KEYS.shiftManagementView
  const [, setLocalStorageView] = useLocalStorage<string>(localStorageKey)

  const params = useParams<{
    viewType: ViewType
    groupType: GroupType
    year: string
    month: string
    day: string
  }>()
  const viewType: ViewType = params.viewType || DEFAULT_VIEW_TYPE
  const groupType: GroupType = params.groupType || DEFAULT_GROUP_TYPE
  const dateFromParams = periodFromParams(params, viewType)

  const view = new View({
    date: dateFromParams,
    viewType,
    groupType,
  })
  const [routeOption, setRouteOption] = React.useState<string>(`${viewType}/${groupType}`)

  useEffect(() => {
    const routeViewType = routeOption.split('/')[0]
    if (routeViewType !== params.viewType) {
      const newRoute = `${params.viewType}/${view.groupType}`
      setRouteOption(newRoute)
      setLocalStorageView(newRoute)
    }
  }, [params.viewType]) // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeRoute = (newRoute: string) => {
    const splitRoute = newRoute.split('/')

    const viewTypeValue: ViewType = isValidViewType(splitRoute[0])
      ? splitRoute[0]
      : DEFAULT_VIEW_TYPE

    const groupTypeValue: GroupType = isValidGroupType(splitRoute[1])
      ? splitRoute[1]
      : DEFAULT_GROUP_TYPE

    setRouteOption(newRoute)
    setLocalStorageView(newRoute)

    const dateFromParams = periodFromParams(params, viewTypeValue)
    const view = new View({
      date: dateFromParams,
      viewType: viewTypeValue,
      groupType: groupTypeValue,
    })

    track(trackingEvents.shiftManagement.selectShiftManagementView, {
      view: view.viewType,
      view_type_by: view.groupType,
      from: 'dropdown_list',
    })

    navigate(
      navigation.shifts.customViewGroup.fullPath({
        viewType: view.viewType,
        groupType: view.groupType,
        year: view.startRangeYear,
        month: view.startRangeMonth,
        day: view.startRangeDay,
      })
    )

    dispatchUI({ type: 'LOADING' })
    dispatchSchedule({ type: 'RESET_VIEW', view })
    dispatchSelection({ type: 'CLEAR_SELECTION' })
    dispatchFilters({ type: 'RESET_SHIFT_IDS' })
    dispatchShiftsRows({ type: 'RESET_SHIFTS_ROWS' })
    dispatchPagination({ type: 'RESET_PAGINATION' })
  }

  const onChangePeriod = (newView: View) => {
    navigate(
      navigation.shifts.customViewGroup.fullPath({
        viewType: newView.viewType,
        groupType: newView.groupType,
        year: newView.startRangeYear,
        month: newView.startRangeMonth,
        day: newView.startRangeDay,
      })
    )

    dispatchUI({ type: 'LOADING' })
    dispatchSchedule({ type: 'RESET_VIEW', view: newView })
    dispatchSelection({ type: 'CLEAR_SELECTION' })
    dispatchFilters({ type: 'RESET_SHIFT_IDS' })
    dispatchShiftsRows({ type: 'RESET_SHIFTS_ROWS' })
    dispatchPagination({ type: 'RESET_PAGINATION' })
  }

  return { view, onChangeRoute, onChangePeriod }
}

export default useChangeView
